import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Box, Button, Checkbox, Typography } from '@mui/material';
import { SingleSelect } from '@ui/components/common/Autocomplete';
import ControlledHCheckboxAutocomplete from '@ui/components/common/Autocomplete/ControlledHCheckboxAutocomplete';
import { ControlledFilledTextField } from '@ui/components/common/TextField';
import { forwardRef, useEffect, useImperativeHandle, useMemo, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import useAddRoleStyles from './useAddRoleStyles';
const getCompanyRegionGroups = (companies) => {
    const regionGroups = {};
    companies.forEach((company) => {
        const regionLabel = company.regionLabel || 'Other';
        if (!regionGroups[regionLabel]) {
            regionGroups[regionLabel] = [];
        }
        regionGroups[regionLabel].push(company.value);
    });
    return regionGroups;
};
const AddEditRoleForm = forwardRef(({ onSubmit, onCancel, dialog = false, edit, role }, ref) => {
    const { t } = useTranslation();
    const styles = useAddRoleStyles();
    const companies = useSelector((state) => state.company.companies);
    const companiesByRegion = useSelector((state) => state.company.companiesByRegion);
    const prevRoleId = useRef();
    const companyRegionLookup = useMemo(() => {
        const lookup = {};
        companiesByRegion.forEach((region) => {
            region.companies.forEach((company) => {
                lookup[company.id] = region.regionLabel || 'Other';
            });
        });
        return lookup;
    }, [companiesByRegion]);
    const companyOptions = useMemo(() => companies.map((c) => ({
        label: c.name,
        value: c.id.toLowerCase(),
        regionLabel: companyRegionLookup[c.id] || 'Other'
    })), [companies, companyRegionLookup]);
    const regionGroups = useMemo(() => getCompanyRegionGroups(companyOptions), [companyOptions]);
    // Determine default values
    const defaultValues = edit && role
        ? {
            id: role.id,
            name: role.name,
            companyId: role.companyId?.toLowerCase() || ''
        }
        : {
            name: '',
            companies: []
        };
    const { control, handleSubmit, reset, formState: { errors } } = useForm({
        defaultValues,
        shouldFocusError: false
    });
    useEffect(() => {
        if (edit && role && role.id !== prevRoleId.current) {
            reset({
                id: role.id,
                name: role.name,
                companyId: role.companyId?.toLowerCase() || ''
            });
            prevRoleId.current = role.id;
        }
        else if (!edit && (!role || role.id !== prevRoleId.current)) {
            reset({
                name: '',
                companies: []
            });
            prevRoleId.current = role?.id;
        }
    }, [edit, role, reset]);
    // Expose submitForm to parent via ref
    useImperativeHandle(ref, () => ({
        submitForm: (redirectToPermissions) => {
            handleSubmit((data) => onSubmit?.(data, redirectToPermissions))();
        }
    }));
    const handleFormSubmit = (data) => {
        if (edit) {
            const updatePayload = {
                id: data.id,
                name: data.name,
                companyId: data.companyId
            };
            onSubmit(updatePayload);
        }
        else {
            const createPayload = {
                name: data.name,
                companies: data.companies || []
            };
            onSubmit(createPayload);
        }
    };
    return (_jsxs(Box, { sx: { ...styles.main, p: dialog ? 2 : 0 }, children: [_jsx(ControlledFilledTextField, { bg: 'grey8', name: 'name', control: control, rules: {
                    required: t('validations.required', { field: t('Role') }),
                    maxLength: {
                        value: 250,
                        message: t('validations.maxLength', {
                            field: t('Name'),
                            max: 250
                        })
                    }
                }, errorMessage: errors.name?.message, label: t('labels.role') }), edit ? (_jsx(SingleSelect, { bg: 'grey8', control: control, label: t('users.company'), name: 'companyId', rules: {
                    required: t('validations.required', { field: t('Company') })
                }, error: errors.companyId?.message, options: companyOptions })) : (_jsx(ControlledHCheckboxAutocomplete, { bg: 'grey8', control: control, label: t('users.company'), rules: {
                    required: t('validations.required', { field: t('Company') })
                }, error: errors.companies?.message, name: 'companies', options: companyOptions, showSelectAll: true, isSortable: false, selectAllLabel: t('actions.selectAll'), groupBy: (option) => option.regionLabel || '', renderGroup: (params) => {
                    const regionLabel = params.group;
                    const regionCompanyIds = regionGroups[regionLabel] || [];
                    const currentCompanies = control._formValues.companies || [];
                    const currentCompanyIds = currentCompanies.map((c) => typeof c === 'object' && c.value ? c.value : c);
                    const allSelected = regionCompanyIds.length > 0 &&
                        regionCompanyIds.every((id) => currentCompanyIds.includes(id));
                    const someSelected = !allSelected && regionCompanyIds.some((id) => currentCompanyIds.includes(id));
                    const isUngrouped = !regionLabel;
                    return (_jsxs("li", { children: [_jsxs(Box, { display: 'flex', justifyContent: 'space-between', alignItems: 'center', sx: { pr: 1 }, onClick: (e) => e.stopPropagation(), children: [_jsx(Typography, { variant: 'subtitle2', sx: { pl: 2, pt: 1, pb: 0.5, color: 'text.secondary' }, children: regionLabel }), !isUngrouped && (_jsx(Checkbox, { size: 'small', checked: allSelected, indeterminate: someSelected, onClick: (e) => {
                                            e.stopPropagation();
                                            if (allSelected) {
                                                const updatedSelection = currentCompanies.filter((c) => {
                                                    const id = typeof c === 'object' && c.value ? c.value : c;
                                                    return !regionCompanyIds.includes(id);
                                                });
                                                control._formValues.companies = updatedSelection;
                                                reset({ ...control._formValues });
                                            }
                                            else {
                                                const newCompanies = companyOptions.filter((opt) => regionCompanyIds.includes(opt.value) &&
                                                    !currentCompanyIds.includes(opt.value));
                                                const updatedSelection = [...currentCompanies, ...newCompanies];
                                                control._formValues.companies = updatedSelection;
                                                reset({ ...control._formValues });
                                            }
                                        }, sx: {
                                            '&.Mui-disabled': {
                                                opacity: 0.5
                                            }
                                        } }))] }), _jsx("ul", { style: { padding: 0, margin: 0 }, children: params.children })] }, params.key));
                } })), !dialog && (_jsxs(Box, { sx: styles.actions, children: [_jsx(Button, { fullWidth: true, onClick: onCancel, variant: 'outlined', color: 'inherit', size: 'large', children: t('actions.cancel') }), _jsx(Button, { fullWidth: true, variant: 'contained', color: 'inherit', onClick: handleSubmit(handleFormSubmit), size: 'large', children: edit ? t('actions.editNow') : t('actions.addNow') })] }))] }));
});
export default AddEditRoleForm;
